import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  getCountFromServer,
} from "firebase/firestore";
import { UserData, UserRole, UserLevel } from "@/types/user";
import { db } from "@/lib/firebase";
import { COLLECTIONS } from "@/constants/firestoreCollections";

function normalizeUser(docId: string, data: Record<string, unknown>): UserData {
  const id = String(data.uid ?? data.id ?? docId);
  return {
    ...data,
    id,
    uid: (data.uid as string) ?? docId,
    email: (data.email as string | null) ?? null,
    displayName: (data.displayName as string | null) ?? (data.name as string | null) ?? null,
    firstName: (data.firstName as string) ?? (data.name as string) ?? "Sin nombre",
    lastName: (data.lastName as string) ?? "",
    phone: data.phone as string | undefined,
    role: (data.role as UserRole) ?? UserRole.CLIENT,
    photoURL: (data.photoURL as string | null) ?? null,
    level: (data.level as UserLevel) ?? UserLevel.BRONZE,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  } as UserData;
}

export async function getAllUsers(): Promise<UserData[]> {
  const snapshot = await getDocs(collection(db, COLLECTIONS.USERS));
  return snapshot.docs.map((d) => normalizeUser(d.id, d.data() as Record<string, unknown>));
}

export async function getUserById(uid: string): Promise<UserData | null> {
  const ref = doc(db, COLLECTIONS.USERS, uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return normalizeUser(snap.id, snap.data() as Record<string, unknown>);
}

export async function createUserInFirestore(uid: string, userData: Partial<UserData>): Promise<void> {
  const ref = doc(db, COLLECTIONS.USERS, uid);
  await setDoc(ref, { ...userData, uid, updatedAt: new Date() });
}

export async function updateUser(uid: string, data: Partial<UserData>): Promise<void> {
  const ref = doc(db, COLLECTIONS.USERS, uid);
  await updateDoc(ref, { ...data, updatedAt: new Date() });
}

export async function deleteUser(uid: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTIONS.USERS, uid));
}

export async function getUserCount(): Promise<number> {
  const snapshot = await getCountFromServer(collection(db, COLLECTIONS.USERS));
  return snapshot.data().count;
}
